'use client';

import { useLanguage } from '@/context/LanguageContext';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useRef, useCallback } from 'react';
import Image from 'next/image';

const projects = [
    {
        id: 1,
        title: 'RC Montana',
        category: 'Site Institucional',
        image: '/portfolio/rc_montana.webp',
    },
    {
        id: 2,
        title: 'Midia Auto',
        category: 'Site de Serviços',
        image: '/portfolio/midia_auto.webp',
    },
    {
        id: 3,
        title: 'Seja Fluente',
        category: 'Página de Lançamento',
        image: '/portfolio/seja_fluente.webp',
    },
    {
        id: 4,
        title: 'Garagem Total Baterias',
        category: 'Loja de Baterias',
        image: '/portfolio/garagem-total.webp',
    },
];

export default function Portfolio() {
    const { t } = useLanguage();
    const carouselRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const scroll = (direction: 'left' | 'right') => {
        if (!carouselRef.current) return;
        const scrollAmount = direction === 'left' ? -450 : 450;
        carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    };

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        if (!carouselRef.current) return;
        isDragging.current = true;
        startX.current = e.pageX - carouselRef.current.offsetLeft;
        scrollLeft.current = carouselRef.current.scrollLeft;
        carouselRef.current.style.cursor = 'grabbing';
        carouselRef.current.style.scrollSnapType = 'none';
    }, []);

    const onMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDragging.current || !carouselRef.current) return;
        e.preventDefault();
        const x = e.pageX - carouselRef.current.offsetLeft;
        const walk = (x - startX.current) * 1.5;
        carouselRef.current.scrollLeft = scrollLeft.current - walk;
    }, []);

    const onMouseUp = useCallback(() => {
        if (!carouselRef.current) return;
        isDragging.current = false;
        carouselRef.current.style.cursor = 'grab';
        carouselRef.current.style.scrollSnapType = 'x mandatory';
    }, []);

    return (
        <section id="portfolio" className="py-24 bg-black relative border-t border-white/5 overflow-hidden">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#19ffb5]/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                <div className="mb-16 flex flex-col md:flex-row items-center justify-between text-center md:text-left gap-6">
                    <div>
                        <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                            {t('portfolio.title.1')} <span className="text-[#19ffb5]">{t('portfolio.title.2')}</span>
                        </h2>
                        <p className="text-lg text-gray-300 max-w-2xl mx-auto md:mx-0 font-light">
                            {t('portfolio.description')}
                        </p>
                    </div>
                    {/* Carousel Controls (Desktop) */}
                    <div className="hidden md:flex gap-4">
                        <button
                            onClick={() => scroll('left')}
                            className="w-12 h-12 rounded-full border border-white/20 bg-white/5 flex items-center justify-center text-white transition-all"
                            aria-label="Scroll Left"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => scroll('right')}
                            className="w-12 h-12 rounded-full border border-white/20 bg-white/5 flex items-center justify-center text-white transition-all"
                            aria-label="Scroll Right"
                        >
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Carousel Container */}
                <div className="relative -mx-6 lg:-mx-8 px-6 lg:px-8">
                    <div
                        ref={carouselRef}
                        className="flex overflow-x-auto gap-6 snap-x snap-mandatory pb-8 cursor-grab select-none"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={onMouseUp}
                        onMouseLeave={onMouseUp}
                    >
                        {projects.map((project) => (
                            <div
                                key={project.id}
                                className="w-[85vw] md:w-[calc(40%-12px)] snap-start shrink-0"
                            >
                                <div className="relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 h-full">
                                    {/* Static Image */}
                                    <div className="relative h-64 w-full">
                                        <Image
                                            src={project.image}
                                            alt={project.title}
                                            fill
                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                            quality={80}
                                            className="object-cover object-top"
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent pointer-events-none z-10" />
                                    </div>

                                    {/* Info */}
                                    <div className="p-6 relative">
                                        <p className="text-[#19ffb5] text-xs font-semibold mb-2 tracking-widest uppercase">{project.category}</p>
                                        <h3 className="text-2xl font-semibold text-white">{project.title}</h3>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Carousel Controls (Mobile Bottom) */}
                <div className="flex md:hidden gap-4 mt-6 justify-center w-full">
                    <button
                        onClick={() => scroll('left')}
                        className="w-12 h-12 rounded-full border border-white/20 bg-white/5 flex items-center justify-center text-white transition-all duration-300"
                        aria-label="Scroll Left"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => scroll('right')}
                        className="w-12 h-12 rounded-full border border-white/20 bg-white/5 flex items-center justify-center text-white transition-all duration-300"
                        aria-label="Scroll Right"
                    >
                        <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            </div>

            <style jsx global>{`
                [data-hide-scrollbar]::-webkit-scrollbar,
                .snap-x::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
        </section>
    );
}
